import { Accordion } from "@/components/ui/accordion";
import { useActiveProjectId } from "@/lib/hooks/useActiveProjectId";
import { useDeploymentLogs } from "@/lib/hooks/useDeploymentLogs";
import { Cloud, ExternalLink, Loader } from "lucide-react";
import type {
  DeploymentStep as DeploymentStepType,
  FpToolCallMessagePart,
} from "../../../../../../schemas/codegen";
import { ExpandableMessageCard } from "../shared";
import { DeploymentStep } from "./DeploymentStep";
import { DeploymentUrl } from "./DeploymentUrl";

interface DeploymentProps {
  part: FpToolCallMessagePart;
}

// Helper function to determine which steps should auto-open
const getAutoOpenSteps = (steps: DeploymentStepType[]): string[] => {
  return steps
    .filter((step) => step.status === "executing" || step.status === "error")
    .map((step) => step.id);
};

export function Deployment({ part }: DeploymentProps) {
  const projectId = useActiveProjectId();

  const deploymentId =
    part.result &&
    typeof part.result === "object" &&
    "data" in part.result &&
    typeof part.result.data === "object" &&
    part.result.data &&
    "deploymentId" in part.result.data &&
    typeof part.result.data.deploymentId === "string"
      ? part.result.data.deploymentId
      : undefined;

  // Initialize deployment logs in store
  const { deployment, isLoading: deploymentsLoading } = useDeploymentLogs(
    projectId,
    deploymentId,
  );

  if (!projectId) {
    // This does not seem to be possible, what do we do here though?
    return <div>No project id was defined</div>;
  }

  if (!deploymentId) {
    // This seems to happen a few times, should we simply not render anything or
    // a spinner?
    return <Loader className="w-4 h-4 animate-spin" />;
  }

  if (deploymentsLoading) {
    return <Loader className="w-4 h-4 animate-spin" />;
  }

  if (!deployment) {
    return <div>Deployment not found</div>;
  }

  // Determine which steps should be auto-opened
  const autoOpenSteps = getAutoOpenSteps(deployment.steps);

  return (
    <ExpandableMessageCard
      status={deployment.status}
      icon={Cloud}
      title={"Deploy"}
      expanded
      accordionValue="deploy"
      hasContentPadding={false}
    >
      <Accordion type="multiple" defaultValue={autoOpenSteps} className="px-0">
        {deployment.steps.map((step) => (
          <DeploymentStep
            key={step.id}
            step={step}
            deploymentId={deployment.id}
          />
        ))}
      </Accordion>

      {deployment.url && (
        <div className="flex items-center justify-between border-t pt-4 px-3">
          <div className="flex items-center gap-3">
            <ExternalLink className="w-4 h-4 flex-shrink-0" />
            <span>API available at</span>
          </div>
          <DeploymentUrl url={deployment.url} />
        </div>
      )}
    </ExpandableMessageCard>
  );
}
